/**
 * First-run tour and setup checklist.
 *
 * Two different jobs, deliberately not merged:
 *
 *   - The **tour** runs once and *does* things rather than pointing at them.
 *     Picking a language, creating the first project and copying an invite
 *     link all happen inside it, so somebody who follows it to the end has a
 *     working workspace rather than a memory of some screenshots. It adapts:
 *     steps that do not apply (a member who cannot invite, an instance that
 *     already has projects) are not shown at all.
 *   - The **checklist** stays. Its ticks are derived from the actual data, not
 *     from "has the user clicked this", so it is honest after a restore, on a
 *     second device, or when somebody else did the work.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../kernel/sync/api';
import { LOCALE_NAMES, localeLabel, useI18n, type Locale, type TranslationKey } from '../../kernel/i18n/i18n';
import { list, useQuery } from '../../kernel/sync/store';
import { pull } from '../../kernel/sync/sync';
import { useMembers, useSession } from '../../kernel/identity/session';
import { THEME_KEY, useTheme } from '../../kernel/design-system/chrome';
import { OverviewDiagram } from './diagrams';
import { Button } from '../../kernel/design-system/ui/button';
import { buttonVariants } from '../../kernel/design-system/ui/button';
import { cn } from '../../kernel/design-system/cn';
import { Input, Select } from '../../kernel/design-system/ui/field';
import { Icon, Sheet, useToast } from '../../kernel/design-system/ui';

const TOUR_KEY = 'kolibri.tour';
const CHECKLIST_KEY = 'kolibri.setup';

/** Other screens ask for the tour by event, so nothing has to own its state. */
export const START_TOUR = 'kolibri:tour';
export const SHOW_CHECKLIST = 'kolibri:setup';

/* ------------------------------------------------------------------ tour */

type Step = 'welcome' | 'prefs' | 'project' | 'invite' | 'done';

export function WelcomeTour() {
  const { t, locale, setLocale } = useI18n();
  const { workspaceId, role, refresh } = useSession();
  const [theme, setTheme] = useTheme();
  const navigate = useNavigate();
  const toast = useToast();

  const [open, setOpen] = useState(() => localStorage.getItem(TOUR_KEY) !== 'done');
  const [index, setIndex] = useState(0);
  const [projectName, setProjectName] = useState('');
  const [created, setCreated] = useState('');
  const [invited, setInvited] = useState(false);
  const [busy, setBusy] = useState(false);

  const canManage = role === 'owner' || role === 'admin';

  useEffect(() => {
    const start = () => {
      setIndex(0);
      setCreated('');
      setInvited(false);
      setOpen(true);
    };
    window.addEventListener(START_TOUR, start);
    return () => window.removeEventListener(START_TOUR, start);
  }, []);

  if (!open || !workspaceId) return null;

  // Only the steps that have something to offer this person. Creating a
  // project and inviting people are both refused for a plain member, so
  // showing them would be showing a wall.
  const steps: Step[] = [
    'welcome',
    'prefs',
    ...(canManage ? (['project', 'invite'] as Step[]) : []),
    'done',
  ];
  const step = steps[Math.min(index, steps.length - 1)];
  const last = index >= steps.length - 1;

  const finish = () => {
    localStorage.setItem(TOUR_KEY, 'done');
    setOpen(false);
  };

  async function createProject(): Promise<void> {
    if (!projectName.trim()) return;
    setBusy(true);
    try {
      const project = await api.post<{ name: string }>(`/api/workspaces/${workspaceId}/projects`, { name: projectName.trim() });
      await pull();
      setCreated(project.name);
    } catch (error) {
      toast(error instanceof Error ? error.message : t('project.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function createInvite(): Promise<void> {
    setBusy(true);
    try {
      const invite = await api.createInvite(workspaceId, 'member');
      await navigator.clipboard?.writeText(`${location.origin}/invite/${invite.code}`);
      setInvited(true);
    } catch (error) {
      toast(error instanceof Error ? error.message : t('common.somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      wide
      title={t('tour.title')}
      onClose={finish}
      footer={
        <>
          <span className="text-muted flex-1 min-w-0 text-[12.5px]">
            {t('tour.stepOf', { current: index + 1, total: steps.length })}
          </span>
          {index > 0 && <Button onClick={() => setIndex(index - 1)}>{t('tour.back')}</Button>}
          {!last && <Button onClick={finish}>{t('tour.skip')}</Button>}
          <Button variant="primary" onClick={() => (last ? finish() : setIndex(index + 1))}>
            {last ? t('tour.finish') : t('tour.next')}
          </Button>
        </>
      }
    >
      {step === 'welcome' && (
        <>
          <h3 className="tour-h">{t('tour.welcomeTitle')}</h3>
          <p className="soft">{t('tour.welcomeBody')}</p>
          <OverviewDiagram />
        </>
      )}

      {step === 'prefs' && (
        <>
          <h3 className="tour-h">{t('tour.prefsTitle')}</h3>
          <p className="soft">{t('tour.prefsBody')}</p>

          <div className="field">
            <label htmlFor="tour-locale">{t('profile.language')}</label>
            <Select
              id="tour-locale"
              value={locale}
              onChange={async (event) => {
                const next = event.target.value as Locale;
                setLocale(next);
                await api.patch('/api/me', { locale: next }).catch(() => undefined);
                await refresh();
              }}
            >
              {(Object.keys(LOCALE_NAMES) as Locale[]).map((value) => <option key={value} value={value}>{localeLabel(value)}</option>)}
            </Select>
          </div>

          <div className="field">
            <label>{t('profile.appearance')}</label>
            <div className="flex items-center gap-1.5">
              {(['system', 'light', 'dark'] as const).map((option) => (
                <button
                  key={option}
                  className={cn(buttonVariants({ size: 'sm' }), theme === option && 'bg-accent text-accent-fg border-accent')}
                  onClick={() => setTheme(option)}
                >
                  <Icon name={option === 'dark' ? 'moon' : option === 'light' ? 'sun' : 'settings'} size={14} />
                  {t(THEME_KEY[option])}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {step === 'project' && (
        <>
          <h3 className="tour-h">{t('tour.projectTitle')}</h3>
          <p className="soft">{t('tour.projectBody')}</p>
          {created ? (
            <p className="tour-ok"><Icon name="check" size={15} /> {t('tour.projectDone', { name: created })}</p>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                placeholder={t('project.namePlaceholder')}
                value={projectName}
                onChange={(event) => setProjectName(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && void createProject()}
              />
              <Button variant="primary" disabled={busy || !projectName.trim()} onClick={() => void createProject()}>
                {busy ? t('project.creating') : t('project.createSubmit')}
              </Button>
            </div>
          )}
        </>
      )}

      {step === 'invite' && (
        <>
          <h3 className="tour-h">{t('tour.inviteTitle')}</h3>
          <p className="soft">{t('tour.inviteBody')}</p>
          {invited ? (
            <p className="tour-ok"><Icon name="check" size={15} /> {t('tour.inviteReady')}</p>
          ) : (
            <Button disabled={busy} onClick={() => void createInvite()}>
              <Icon name="link" size={14} /> {t('members.createInvite')}
            </Button>
          )}
        </>
      )}

      {step === 'done' && (
        <>
          <h3 className="tour-h">{t('tour.doneTitle')}</h3>
          <p className="soft">{t('tour.doneBody')}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => {
                finish();
                navigate('/guide');
              }}
            >
              <Icon name="help" size={14} /> {t('tour.openGuide')}
            </Button>
          </div>
        </>
      )}
    </Sheet>
  );
}

/* ------------------------------------------------------------- checklist */

interface Item {
  id: string;
  done: boolean;
  label: TranslationKey;
  hint: TranslationKey;
  cta: TranslationKey;
  run: () => void;
}

export function SetupChecklist() {
  const { t } = useI18n();
  const { workspaceId, role } = useSession();
  const navigate = useNavigate();
  const members = useMembers();
  const [hidden, setHidden] = useState(() => localStorage.getItem(CHECKLIST_KEY) === 'hidden');
  const [allowSignup, setAllowSignup] = useState<boolean | null>(null);

  const canManage = role === 'owner' || role === 'admin';

  const counts = useQuery(() => ({
    projects: list('project', (p) => p.workspace_id === workspaceId && !p.archived).length,
    tasks: list('task', (task) => task.workspace_id === workspaceId).length,
    pages: list('page', (page) => page.workspace_id === workspaceId && !page.archived).length,
  }), [workspaceId]);

  useEffect(() => {
    if (!canManage) return;
    api.config().then((config) => setAllowSignup(config.allowSignup)).catch(() => setAllowSignup(null));
  }, [canManage]);

  useEffect(() => {
    const show = () => {
      localStorage.removeItem(CHECKLIST_KEY);
      setHidden(false);
    };
    window.addEventListener(SHOW_CHECKLIST, show);
    return () => window.removeEventListener(SHOW_CHECKLIST, show);
  }, []);

  const items: Item[] = [
    {
      id: 'project', done: counts.projects > 0,
      label: 'setup.project', hint: 'setup.projectHint', cta: 'setup.projectCta',
      run: () => navigate('/projects/new'),
    },
    {
      id: 'task', done: counts.tasks > 0,
      label: 'setup.task', hint: 'setup.taskHint', cta: 'setup.taskCta',
      // AppShell owns the quick-add sheet; asking for it by event keeps the
      // checklist from having to be mounted inside it.
      run: () => window.dispatchEvent(new CustomEvent('kolibri:new-task')),
    },
    {
      id: 'page', done: counts.pages > 0,
      label: 'setup.page', hint: 'setup.pageHint', cta: 'setup.pageCta',
      run: () => navigate('/pages'),
    },
    ...(canManage ? [
      {
        id: 'invite', done: members.length > 1,
        label: 'setup.invite' as TranslationKey, hint: 'setup.inviteHint' as TranslationKey, cta: 'setup.inviteCta' as TranslationKey,
        run: () => navigate('/settings?tab=members'),
      },
      {
        id: 'signup', done: allowSignup === false,
        label: 'setup.signup' as TranslationKey, hint: 'setup.signupHint' as TranslationKey, cta: 'setup.signupCta' as TranslationKey,
        run: () => navigate('/settings?tab=workspace'),
      },
    ] : []),
  ];

  const done = items.filter((item) => item.done).length;
  // Nothing left to nag about, and nothing to nag someone who dismissed it.
  if (hidden || done === items.length) return null;

  return (
    <section className="rounded-[var(--radius)] border border-line bg-raised p-3.5 setup" aria-label={t('setup.title')}>
      <div className="flex items-center gap-2 mb-2.5">
        <strong className="text-[13.5px]">{t('setup.title')}</strong>
        <span className="text-muted text-[12.5px]">{t('setup.progress', { done, total: items.length })}</span>
        <span className="flex-1 min-w-0" />
        <Link className={buttonVariants({ variant: 'ghost', size: 'sm' })} to="/guide"><Icon name="help" size={13} /> <span className="hide-sm">{t('nav.guide')}</span></Link>
        <Button variant="ghost" size="sm"
          onClick={() => {
            localStorage.setItem(CHECKLIST_KEY, 'hidden');
            setHidden(true);
          }}
        >
          {t('setup.hide')}
        </Button>
      </div>

      <div className="progress mb-3">
        <i style={{ width: `${(done / items.length) * 100}%` }} />
      </div>

      {items.map((item) => (
        <div className={`setup-item${item.done ? ' done' : ''}`} key={item.id}>
          <span className="setup-tick">{item.done && <Icon name="check" size={12} />}</span>
          <span className="flex-1 min-w-0">
            <span className="setup-label">{t(item.label)}</span>
            <span className="setup-hint">{t(item.hint)}</span>
          </span>
          {!item.done && <Button size="sm" onClick={item.run}>{t(item.cta)}</Button>}
        </div>
      ))}
    </section>
  );
}
